import { inject, Injectable } from '@angular/core';
import mqtt from 'mqtt';
import { BehaviorSubject, Subject } from 'rxjs';
import { makeid, wait } from '../helper/api-helper';
import { getProductByModelIdentifier } from '../ECoSystemsHelper';
import { getSyncManagerByModelIdentifierAndVersion } from '../products/SyncFactory';
import { Pool } from '../../../apis/pool.api';
import { IProduct } from '../products/IProduct';
import { RxTxDescriptor } from '../products/IMasterSync';
import { EdroNetworkState } from '../helper/enum/EdroNetworkState';
import { IMaster } from '../products/IMaster';
import { EdroNetworkManager } from './edro-network-manager';
import { Buffer } from 'buffer';

export enum MQTTState {
  NotConnected = 'NotConnected',
  ConnectionProcess = 'ConnectionProcess',
  Connected = 'Connected',
}

@Injectable({
  providedIn: 'root',
})
export class MQTTNetworkManager {
  private _mqttState: MQTTState = MQTTState.NotConnected;
  private _client: mqtt.MqttClient | null = null;

  private networkManager: EdroNetworkManager | null = null;

  public onServiceStateChange = new BehaviorSubject<MQTTState>(
    MQTTState.NotConnected,
  );
  public onDataChange = new Subject<string>();

  private bipInterval: any = null;
  private randomId: string = '';
  private isUpToDateInterval: any = null;
  private lastIsUpToDateMessageDate: number = 0;
  private slowNetwork: any = null;
  private syncManager: any = null;
  private messagesStack: any = [];
  public stopRunningProcess: boolean = false;
  private initTicket: any = null;

  constructor() {
    this.randomId = makeid(5);
  }

  public setEdroNetworkManager(networkManager: EdroNetworkManager) {
    this.networkManager = networkManager;
  }

  public init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this._mqttState !== MQTTState.NotConnected) {
        reject(
          'Wrong state this._mqttState !== MQTTState.NotConnected ' +
            this._mqttState,
        );
        return;
      }

      const context = this.networkManager!.getPoolContext();

      if (!context?.pool?.scalewayId) {
        reject('Scaleway Id missing ' + this._mqttState);
        return;
      }

      this.changeMqttState(MQTTState.ConnectionProcess);

      // Configuration pour MQTT over WebSocket Secure (WSS)
      const brokerUrl = 'wss://websocket.iot.fr-par.scw.cloud:443'; // Port WSS standard pour MQTT

      const options: mqtt.IClientOptions = {
        clientId: context!.pool!.scalewayId! + '-' + this.randomId,
        username: context!.pool!.scalewayId!,
        password: '',
        connectTimeout: 2000,
        path: '/mqtt',
        reconnectPeriod: 0, // Désactive la reconnexion automatique
      };

      console.log(options);

      this._client = mqtt.connect(brokerUrl, options);

      let timeout = setTimeout(() => {
        timeout = null as any;
        this.changeMqttState(MQTTState.NotConnected);
        reject('Connect Timeout');
      }, 3000);

      this._client.on('connect', () => {
        console.log('MQTT WebSocket Connected');

        if (timeout == null) {
          return;
        }

        clearTimeout(timeout);

        if (this.stopRunningProcess) {
          this.resetNow(false);
          reject('Network Manager ask stop process');
          return;
        }

        console.log('MQTT WebSocket Connected');

        this.startSignalement()
          .then(() => {
            this.changeMqttState(MQTTState.Connected);
            resolve();
          })
          .catch((error) => {
            console.log(error);
            this.changeMqttState(MQTTState.NotConnected);
            this.resetNow(false);
            reject(error);
          });
      });

      this._client.on('message', (topic: string, message: Buffer) => {
        this.onMessage(topic, message);
      });

      this._client.on('error', (error) => {
        if (timeout) {
          clearTimeout(timeout);
          this.changeMqttState(MQTTState.NotConnected);
          reject(error.message);
        }
        this.onError(error.message);
      });

      this._client.on('close', () => {
        this.onDisconnect('Connection closed');
      });
    });
  }

  private async sendSubscribe(pool: any, max: number): Promise<boolean> {
    if (!this._client) return false;

    for (let i = 0; i <= max; i++) {
      const indexBuffer = Buffer.allocUnsafe(2);
      indexBuffer.writeUInt16LE(i, 0);

      this._client.subscribe(
        [
          pool!.deviceTable.master.serialNumber +
            '/CTX/DATA/' +
            indexBuffer.toString('hex').toUpperCase(),
        ],
        { qos: 1 },
      );

      await wait(5);
    }
    return true;
  }

  private async startSignalement(): Promise<boolean> {
    const pool = this.networkManager!.getPoolContext()!.pool;

    this.syncManager = null;
    this.messagesStack = [];

    if (!this._client) {
      throw new Error('MQTT client not initialized');
    }

    // S'abonner au topic de connexion
    this._client.subscribe(
      [pool!.deviceTable.master.serialNumber + '/CTX/connexion'],
      { qos: 1 },
    );

    // Démarrer le signalement de présence
    this.sendBip();

    if (this.bipInterval) {
      clearInterval(this.bipInterval);
    }
    this.bipInterval = setInterval(() => this.sendBip(), 1000);

    this.lastIsUpToDateMessageDate = 0;

    return new Promise<boolean>((resolve, reject) => {
      let startDetectDate = new Date().getTime();
      let detectPresenceConnect = false;
      let subscribeToData = false;

      let interval = setInterval(() => {
        if (this.stopRunningProcess) {
          clearInterval(interval);
          reject('Network Manager ask stop process');
          return;
        }

        if (
          detectPresenceConnect == false &&
          this.lastIsUpToDateMessageDate > 0
        ) {
          detectPresenceConnect = true;

          this._client?.subscribe(
            [pool!.deviceTable.master.serialNumber + '/CTX/DT/#'],
            { qos: 1 },
          );
        }

        if (subscribeToData == false && this.syncManager != null) {
          subscribeToData = true;
          this.sendSubscribe(pool, this.syncManager.getTotalDataVector());
        }

        if (
          detectPresenceConnect == true &&
          this.syncManager &&
          this.syncManager!.isSync
        ) {
          clearInterval(interval);

          if (this.isUpToDateInterval) {
            clearInterval(this.isUpToDateInterval);
          }

          this.isUpToDateInterval = setInterval(
            () => this.checkIsUpToDate(),
            1000,
          );
          resolve(true);
        } else if (new Date().getTime() - startDetectDate >= 10 * 1000) {
          clearInterval(interval);

          if (detectPresenceConnect == false) {
            reject('Finalize connexion failed : No Presence Connect Received');
          } else {
            console.log(
              this.syncManager?.isSync,
              this.syncManager?.deviceTableNotSyncDescriptors(),
              this.syncManager?.dataNotSyncDescriptors(),
            );

            reject('Finalize connexion failed : Synchro issue');
          }
        }
      }, 100);
    });
  }

  private checkIsUpToDate() {
    if (new Date().getTime() - this.lastIsUpToDateMessageDate >= 3 * 1000) {
      console.log(
        'Mqtt Reset : No Presence connect since ' +
          (new Date().getTime() - this.lastIsUpToDateMessageDate),
      );
      this.resetNow();
      this.changeMqttState(MQTTState.NotConnected);
    }
  }

  private sendBip() {
    if (!this._client) return;

    const pool = this.networkManager!.getPoolContext()!.pool;
    const timestamp = new Date().getTime();

    this._client.publish(
      pool!.deviceTable.master.serialNumber + '/CRX/connexion',
      Buffer.from('bip-' + this.randomId + ';' + timestamp),
      { qos: 1 },
    );

    console.log('Bip Send', new Date());
  }

  private onMessage(topic: string, message: Buffer) {
    const pool = this.networkManager!.getPoolContext()!.pool;

    if (topic.startsWith(pool!.deviceTable.master.serialNumber + '/CTX/')) {
      this.lastIsUpToDateMessageDate = new Date().getTime();
    }

    try {
      if (
        topic.startsWith(
          pool!.deviceTable.master.serialNumber + '/CTX/connexion',
        )
      ) {
        console.log('Presence Connect', message.toString(), new Date());

        const strMessage = message.toString();
        const propagationTime =
          new Date().getTime() - parseInt(strMessage.split(';')[1]);

        if (
          strMessage.startsWith('bip-' + this.randomId) &&
          strMessage.split(';').length === 2 &&
          propagationTime > 1000
        ) {
          this.slowNetwork = (propagationTime / 1000).toFixed(1);
        } else {
          this.slowNetwork = null;
        }
        return;
      }
      // ... reste de la logique de traitement des messages identique
      else if (
        topic.startsWith(
          pool!.deviceTable.master.serialNumber + '/CTX/etatFIFO',
        )
      ) {
        console.log('Receive etatFIFO = ' + message);
        return;
      } else if (
        topic.startsWith(pool!.deviceTable.master.serialNumber + '/CTX/')
      ) {
        if (this.syncManager === null) {
          const deviceDescriptor = topic.split('/');
          const index = Buffer.from(deviceDescriptor[3], 'hex').readInt16LE(0);
          const buffer = Buffer.from(message.toString(), 'hex');

          this.messagesStack.push({ topic: topic, message: message });

          if (deviceDescriptor[2] === 'DT' && index === 1) {
            const master = getProductByModelIdentifier(
              pool!.deviceTable.master.modelIdentifier,
            ) as IMaster;
            const firmwareVersion = buffer.readUInt8(15);
            this.syncManager = getSyncManagerByModelIdentifierAndVersion(
              master.modelIdentifier!,
              firmwareVersion,
            );
            this.networkManager!.setMasterSyncManager(this.syncManager);

            for (let i = 0; i < this.messagesStack.length; i++) {
              this.applyMessage(
                this.messagesStack[i].topic,
                this.messagesStack[i].message,
                pool!,
              );
            }

            this.messagesStack = [];
          }
        } else {
          this.applyMessage(topic, message, pool!);
        }
      }
    } catch (e) {
      console.log(e);
    }
  }

  private applyMessage(topic: string, message: Buffer, pool: Pool) {
    // Logique identique au code original mais adaptée pour Buffer
    const syncManager = this.networkManager!.getMasterSyncManager();
    const deviceDescriptor = topic.split('/');

    console.log('RECEIVE MQTT', topic);

    const index = Buffer.from(deviceDescriptor[3], 'hex').readInt16LE(0);
    const buffer = Buffer.from(message.toString(), 'hex');

    console.log('RECEIVE MQTT', index, buffer);

    if (deviceDescriptor[2] === 'DT') {
      syncManager?.addBackup(true, deviceDescriptor[3], message.toString());
    } else if (deviceDescriptor[2] === 'DATA') {
      syncManager?.addBackup(false, deviceDescriptor[3], message.toString());
    }

    if (deviceDescriptor[2] === 'DT') {
      if (syncManager?.getDeviceTableDescriptorByIndex(index) !== null) {
        syncManager?.deviceTableMarkAsSync(index);
      }

      const descriptor = syncManager?.getDeviceTableDescriptorByIndex(index);

      if (descriptor != null && descriptor.notify != null) {
        const paddingBuffer = Buffer.alloc(100);
        const safeBuffer = Buffer.concat([buffer, paddingBuffer], 100);
        descriptor.notify(pool!, index, safeBuffer);
      }
    } else if (deviceDescriptor[2] === 'DATA') {
      if (syncManager?.getDataDescriptorByIndex(index) !== null) {
        syncManager?.dataMarkAsSync(index);
      }

      const descriptor = syncManager?.getDataDescriptorByIndex(index);

      if (descriptor != null && descriptor.notify != null) {
        const paddingBuffer = Buffer.alloc(100);
        const safeBuffer = Buffer.concat([buffer, paddingBuffer], 100);
        descriptor.notify(pool!, index, safeBuffer);
      }
    }

    if (syncManager!.isSync === true) {
      this.onDataChange.next('change');
    }
  }

  public async askDataWrite(
    productClass: IProduct,
    index: any,
    pool: Pool,
  ): Promise<true> {
    if (!this.networkManager!.getMasterSyncManager()) {
      return Promise.reject('No sync manager');
    }

    if (!this._client) {
      return Promise.reject('MQTT client not connected');
    }

    const info: RxTxDescriptor | null =
      this.networkManager!.getMasterSyncManager()!.getDataDescriptorByIndex(
        index,
      );

    if (!info) {
      return Promise.reject(
        'No descriptor for this index : ' +
          index +
          ' ' +
          productClass.modelIdentifier,
      );
    }

    if (!info.write) {
      return Promise.reject('No write descriptor for this index : ' + index);
    }

    const writeBuffer = info.write(productClass, pool, index);

    console.log('SEND MQTT', index, writeBuffer);

    const indexBuffer = Buffer.allocUnsafe(2);
    indexBuffer.writeUInt16LE(index, 0);

    this._client.publish(
      pool!.deviceTable.master.serialNumber +
        '/CRX/DATA/' +
        indexBuffer.toString('hex').toUpperCase(),
      Buffer.from(writeBuffer!.toString('hex').toUpperCase()),
      { qos: 1 },
    );

    return Promise.resolve(true);
  }

  private onConnect() {
    console.log('Mqtt onConnect');
  }

  private onError(error: string) {
    console.log('Mqtt Error:' + error);
  }

  private onDisconnect(cause: string) {
    console.log('Mqtt Disconnect:' + cause);
    this.resetNow();
    this.changeMqttState(MQTTState.NotConnected);
  }

  private changeMqttState(newState: MQTTState) {
    this._mqttState = newState;
    this.onServiceStateChange.next(newState);
  }

  private resetNow(removeTicket = true) {
    if (removeTicket) {
      this.initTicket = null;
    }

    this.networkManager!.getMasterSyncManager()?.resetSync();

    if (this._client) {
      this._client.removeAllListeners();
      if (this._mqttState !== MQTTState.NotConnected) {
        this._client.end();
      }
    }

    if (this.bipInterval) {
      clearInterval(this.bipInterval);
      this.bipInterval = null;
    }

    if (this.isUpToDateInterval) {
      clearInterval(this.isUpToDateInterval);
      this.isUpToDateInterval = null;
    }

    this.lastIsUpToDateMessageDate = 0;
  }

  public reset(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resetNow();
      setTimeout(() => {
        this.changeMqttState(MQTTState.NotConnected);
        resolve();
      }, 100);
    });
  }

  public getServiceState() {
    if (this._mqttState === MQTTState.Connected) {
      return EdroNetworkState.Running;
    } else if (this._mqttState === MQTTState.ConnectionProcess) {
      return EdroNetworkState.Pending;
    } else {
      return EdroNetworkState.Stop;
    }
  }
}
